import {
  Box,
  Button,
  Typography,
  useTheme,
  Grid,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

import Header from "../../components/Header";
import { tokens } from "../../theme/tokens";
import undraw1 from "../../assets/undraw_instant-analysis_vm8x_gold.svg";

const About = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const Section = ({ children }) => (
    <Box
      my={6}
      p={4}
      backgroundColor="background.paper"
      borderRadius="8px"
    >
      {children}
    </Box>
  );

  const IllustrationPlaceholder = ({ title, description }) => (
    <Box
      sx={{
        border: "1px dashed",
        borderColor: colors.grey?.[500] || "grey.500",
        borderRadius: "8px",
        p: 3,
        minHeight: "220px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        textAlign: "center",
        opacity: 0.8,
      }}
    >
      <Typography
        variant="h6"
        sx={{ color: colors.houndGold?.[500] }}
      >
        {title}
      </Typography>

      <Typography variant="body2" mt={1}>
        {description}
      </Typography>
    </Box>
  );

  return (
    <Box>
      <Header
        title="ABOUT OPERATIONAL CLARITY"
        subtitle="Practical operational thinking for growing independent businesses."
      />

<Section>
  <Grid container spacing={4} alignItems="center">
    <Grid item xs={12} md={7}>
      <Typography
        variant="h3"
        gutterBottom
        sx={{ color: colors.houndGold?.[500] }}
      >
        I spent years fixing operational problems before I ever wrote software.
      </Typography>

      <Typography variant="body1">
        Long before I became a software engineer, I managed businesses where
        keeping operations running every day wasn't optional, it was the job.
        <br />
        <br />
        Running wholesale operations, managing teams, dealing with customers,
        and solving problems as they happened, I saw first-hand how easily
        businesses become dependent on the knowledge of a few experienced
        people.
        <br />
        <br />
        Later, after retraining as a software engineer and leading backend
        systems professionally, I realised something surprising.
        <br />
        <br />
        The same problems existed everywhere.
      </Typography>
    </Grid>

    <Grid item xs={12} md={5}>
      <Box
        sx={{
          backgroundColor: colors.scalpelTeal[500],
          borderRadius: "12px",
          p: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <img
          src={undraw1}
          alt="Workflow Illustration"
          style={{
            width: "100%",
            maxWidth: "340px",
            opacity: 0.95,
          }}
        />
      </Box>
    </Grid>
  </Grid>
</Section>

<Section>
  <Typography
    variant="h4"
    gutterBottom
    sx={{ color: colors.houndGold?.[500] }}
  >
    The same operational patterns appear everywhere.
  </Typography>

  <Typography variant="body1" mb={3}>
    Not just in warehouses or bike shops.
    <br />
    <br />
    In software companies. Manufacturing. Hospitality. Professional services.
    <br />
    <br />
    Different industries. Exactly the same operational patterns.
  </Typography>

  <List>
    <ListItem disableGutters dense>
      <ListItemText primary="Knowledge trapped in people's heads." />
    </ListItem>

    <ListItem disableGutters dense>
      <ListItemText primary="Processes that evolved over years but were never documented." />
    </ListItem>

    <ListItem disableGutters dense>
      <ListItemText primary="Managers becoming the answer to every question." />
    </ListItem>

    <ListItem disableGutters dense>
      <ListItemText primary="Businesses growing faster than their operational visibility." />
    </ListItem>
  </List>

  <Typography variant="body1" mt={3}>
    Operational Clarity is my attempt to solve those problems using practical
    operational thinking - not management theory.
  </Typography>
</Section>

<Section>
  <Typography
    variant="h4"
    gutterBottom
    sx={{ color: colors.houndGold?.[500] }}
  >
    What I'm building
  </Typography>

  <Typography variant="body1" mb={3}>
    I'm working with growing businesses to understand where operational friction
    really comes from.
    <br />
    <br />
    Together we identify hidden risks, document critical knowledge, improve
    visibility, and build systems that make day-to-day operations easier.
    <br />
    <br />
    Every conversation teaches me something.
    <br />
    <br />
    Every business operates differently.
    <br />
    <br />
    The goal isn't to impose a framework.
    <br />
    <br />
    It's to uncover what makes your business work - and make that knowledge
    easier to share.
  </Typography>
</Section>

<Section>
  <Typography
    variant="h4"
    gutterBottom
    sx={{ color: colors.houndGold?.[500] }}
  >
    Why this matters
  </Typography>

  <Typography variant="body1">
    I don't believe businesses struggle because people aren't working hard
    enough.
    <br />
    <br />
    Most teams work incredibly hard.
    <br />
    <br />
    The problem is that the business becomes harder to see.
    <br />
    <br />
    When knowledge is scattered across people, emails, conversations and habit,
    complexity grows faster than clarity.
    <br />
    <br />
    Operational Clarity exists to reverse that trend.
  </Typography>

    <Button variant="contained" color="secondary" size="large" href="/contact">
    Let's have a conversation
  </Button>
</Section>

      <Box mt={8} textAlign="center">
        <Typography variant="body2">
          Barry Marples - Operational Clarity
        </Typography>

        <Typography variant="caption" display="block" mt={2}>
          Clarity in operations • Alignment in teams • Confidence in decisions
        </Typography>
      </Box>
    </Box>
  );
};

export default About;